import {
    exportCompareInChart,
    generateScopeReport,
    getCompareInChart,
    getScope,
    type ICompareInChart,
    type IExportBase,
    type IScope,
} from "@/api";
import { ExportChartModal } from "@/components/frontend/ExportChartModal";
import { ExportScopeModal } from "@/components/frontend/ExportScopeModal";
import { comparisonHasFields } from "@/utils/comparison";
import { useCallback, useState, type ReactElement } from "react";

const XLSX_MIME =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function downloadBlob(blob: Blob, filename: string, mimeType: string): void {
    const url = window.URL.createObjectURL(
        new Blob([blob], { type: mimeType })
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

function stripFileExtension(title: string): string {
    return title.replace(/\.[^/.]+$/, "");
}

interface AttachmentExportModalsHook {
    triggerScopeExport: (scopeId: number) => Promise<void>;
    triggerComparisonExport: (comparisonId: number) => Promise<void>;
    /** Render alongside the component using the hook. */
    exportModalsElement: ReactElement;
}

/**
 * Manages export modal state + submission for scope and comparison attachments.
 * Used by any surface that renders AttachmentPillRow and supports the
 * "Export" action for scope/comparison reference types.
 */
export function useAttachmentExportModals(): AttachmentExportModalsHook {
    const [exportScope, setExportScope] = useState<IScope | null>(null);
    const [exportScopeOpen, setExportScopeOpen] = useState(false);
    const [exportComparison, setExportComparison] =
        useState<ICompareInChart | null>(null);
    const [exportChartOpen, setExportChartOpen] = useState(false);

    const triggerScopeExport = useCallback(async (scopeId: number) => {
        try {
            const scope = await getScope(scopeId);
            setExportScope(scope);
            setExportScopeOpen(true);
        } catch (error) {
            console.error("Failed to fetch scope for export:", error);
        }
    }, []);

    const triggerComparisonExport = useCallback(
        async (comparisonId: number) => {
            try {
                const comparison = await getCompareInChart(
                    String(comparisonId)
                );
                setExportComparison(comparison);
                setExportChartOpen(true);
            } catch (error) {
                console.error("Failed to fetch comparison for export:", error);
            }
        },
        []
    );

    const handleScopeExportSubmit = async (exportData: IExportBase) => {
        if (!exportScope) return;
        try {
            const blob = await generateScopeReport(exportScope.id, exportData);
            const filename = `${stripFileExtension(exportScope.document_title)}.xlsx`;
            downloadBlob(blob, filename, XLSX_MIME);
            setExportScopeOpen(false);
        } catch (error) {
            console.error("Scope export failed:", error);
        }
    };

    const handleComparisonExportSubmit = async (exportData: IExportBase) => {
        if (!exportComparison) return;
        try {
            const blob = await exportCompareInChart(
                exportComparison.id,
                exportData
            );
            const filename = `comparison_${exportComparison.id}.xlsx`;
            downloadBlob(blob, filename, XLSX_MIME);
            setExportChartOpen(false);
        } catch (error) {
            console.error("Comparison export failed:", error);
        }
    };

    const exportModalsElement = (
        <>
            {exportScope && (
                <ExportScopeModal
                    open={exportScopeOpen}
                    onClose={() => setExportScopeOpen(false)}
                    scope={exportScope}
                    onExport={handleScopeExportSubmit}
                />
            )}
            {exportComparison && (
                <ExportChartModal
                    open={exportChartOpen}
                    onClose={() => setExportChartOpen(false)}
                    scopes={exportComparison.scopes}
                    hasFields={comparisonHasFields(exportComparison)}
                    onExport={handleComparisonExportSubmit}
                />
            )}
        </>
    );

    return {
        triggerScopeExport,
        triggerComparisonExport,
        exportModalsElement,
    };
}
